import { Fragment, useState, useEffect } from 'react'
import { last } from 'lodash'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { appendList, st } from 'lib/helpers'
import { ROUTE_PATH } from 'lib/paths'
import { Post } from 'lib/_types'

import { useBreadcrumb, useInfinityScrollSimple, usePostActions } from 'hooks'

import { useGetHashtagTimelineQuery } from 'store/api/yay/timeline'

import { useNavigate } from 'features/i18n/router'

import { useOrderPostsByReplies } from 'features/Post/helpers'
import SeoHeader from 'components/SEOHeader'
import PostItem from 'components/Post/PostItem'
import PostItemSkeleton from 'components/Post/PostItem/PostItemSkeleton'
import LoadMore from 'components/LoadMore'
import BoxHeader from 'components/BoxHeader'

const Hashtag = () => {
  const { hashtag } = useParams()
  const navigate = useNavigate()
  const { setBreadcrumb } = useBreadcrumb()
  const { t } = useTranslation()

  const [__fromId, __fromIdSet] = useState<string>('0')
  const [__posts, __postsSet] = useState<Array<Post>>([])

  const postActions = usePostActions({
    postList: __posts,
    setter: __postsSet,
  })

  const hashtagTimelineRes = useGetHashtagTimelineQuery({
    fromPostId: __fromId,
    hashtag,
  })

  const loadMore = () => {
    if (!hashtagTimelineRes?.isFetching) {
      __fromIdSet(last(__posts)?.id?.toString?.())
    }
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    return () => {
      __postsSet([])
    }
  }, [hashtag])

  useEffect(() => {
    if (
      hashtagTimelineRes?.isSuccess &&
      !hashtagTimelineRes?.isFetching &&
      hashtagTimelineRes?.currentData
    ) {
      __postsSet((prevPosts) =>
        appendList(prevPosts, hashtagTimelineRes?.currentData)
      )
    }
  }, [hashtagTimelineRes])

  useEffect(() => {
    setBreadcrumb([
      {
        title: t('common.timeline'),
        link: ROUTE_PATH.TIMELINE_PUBLIC,
      },
      { title: hashtag },
    ])
  }, [hashtag])

  const postsOrderedByReplies = useOrderPostsByReplies({
    posts: __posts,
  })

  return (
    <Fragment>
      <SeoHeader
        description={st(`hashtag.description`, { hashtag })}
        title={st(`hashtag.title`, { hashtag })}
      />

      <div className="Box Timeline">
        <BoxHeader
          nav={[
            {
              label: t('common.back'),
              isBack: true,
              onClick: async (e: any) => {
                e.preventDefault()
                await navigate(-1)
              },
            },
          ]}
          headerClass="Box__header--with-hashtag"
          afterNav={<p className="Box__hashtag">#{hashtag}</p>}
        />

        <div className="PostList">
          {postsOrderedByReplies?.map?.((x, i) => (
            <PostItem
              key={x?.post?.id}
              post={x?.post}
              index={i}
              isConversationLayout={x?.isConversationLayout}
              isConversationLayoutSmall={x?.isConversationLayoutSmall}
              hasSeeMore={x?.hasSeeMore}
              isLast={i === (postsOrderedByReplies?.length ?? 0) - 1}
              {...postActions}
            />
          ))}

          {!hashtagTimelineRes?.isFetching &&
            postsOrderedByReplies?.length === 0 && (
              <div className="SearchPage__empty">
                <strong>{t('posts_search.not_found.title')}</strong>

                <p>
                  {t('posts_search.not_found.description', {
                    keyword: '#' + hashtag,
                  })}
                </p>
              </div>
            )}

          {hashtagTimelineRes?.isFetching && <PostItemSkeleton />}

          <LoadMore
            isLoading={hashtagTimelineRes?.isFetching}
            hidden={!hashtagTimelineRes?.currentData?.length}
            onClick={() => {
              loadMore()
            }}
            ref={loadMoreRef}
          />
        </div>
      </div>
    </Fragment>
  )
}

export default Hashtag
